import React from "react";
import { Dialog, Stack, Button, DialogContent } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  usePlatformCustomCoverQuery,
  useUpdatePlatformCustomCoverMutation,
} from "@/partner/entities/platforms";
import {
  CustomCoverProvider,
  useCustomCover,
} from "@/partner/features/platformCustomCovers";
import { DialogHeader } from "@/shared/components";
import { CustomCoverPreview } from "./CustomCoverPreview";
import { EditDrawer } from "./EditDrawer";
import { EditorToolbar } from "./EditorToolbar";
import { DeviceModeToolbar } from "./DeviceModeToolbar";

const Content = styled("div")({
  overflow: "hidden",
  minHeight: 600,
});

/** Состояние обложки, от которого зависят подписи кнопок редактора. */
type CoverEditorState = "draft" | "published";

const PRIMARY_ACTION_LABEL_KEYS: Record<CoverEditorState, string> = {
  draft: "common.actions.publish",
  published: "common.actions.save",
};

const SECONDARY_ACTION_LABEL_KEYS: Record<CoverEditorState, string> = {
  draft: "common.actions.save",
  published: "platformCustomCovers.editor.toDraft",
};

interface CustomCoverEditorInnerProps {
  coverId: string;
  onClose: () => void;
  onSave: () => void;
}

const CustomCoverEditorInner: React.FC<CustomCoverEditorInnerProps> = ({
  coverId,
  onClose,
  onSave,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const {
    coverId: contextCoverId,
    onSetupSelectedCover,
    onResetCoverState,
    title,
    subTitle,
    buttons,
    backgrounds,
    brightness,
    publishAt,
    onChangePublishAt,
    buttonsColor,
    buttonsSize,
    align,
    alignMobile,
    verticalAlign,
    verticalAlignMobile,
  } = useCustomCover();

  const { loading } = usePlatformCustomCoverQuery({
    variables: { id: coverId },
    skip: !coverId,
    onCompleted: (data) => {
      if (data?.platformCustomCover) {
        onSetupSelectedCover(data.platformCustomCover);
      }
    },
  });

  const [updateCover, { loading: updating }] =
    useUpdatePlatformCustomCoverMutation({
      onCompleted: () => {
        enqueueSnackbar(t("platformCustomCovers.snackbar.updated"), {
          variant: "success",
        });
        onSave();
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["PlatformCustomCovers"],
    });

  const isPublished = Boolean(publishAt);
  const state: CoverEditorState = isPublished ? "published" : "draft";

  const handleClose = () => {
    onClose();
    onResetCoverState();
  };

  const handleSave = async (publish: boolean) => {
    if (!contextCoverId) {
      enqueueSnackbar(t("platformCustomCovers.snackbar.notFound"), {
        variant: "error",
      });
      return;
    }

    const input: any = {
      title: {
        text: title.text || "",
        color: title.color || "",
        size: title.size || "",
        weight: title.weight || "",
      },
      subTitle: {
        text: subTitle.text || "",
        color: subTitle.color || "",
        size: subTitle.size || "",
        weight: subTitle.weight || "",
      },
      buttons: buttons.map((button) => ({
        text: button.text || "",
        link: button.link || "",
        variant: button.variant || "",
        disabled: button.disabled || false,
      })),
      background: {
        mobileImageId: backgrounds.mobileImageId || null,
        desktopImageId: backgrounds.desktopImageId || null,
        mobileVideoId: backgrounds.mobileVideoId || null,
        desktopVideoId: backgrounds.desktopVideoId || null,
      },
      brightness,
      buttonsColor,
      buttonsSize,
      align,
      alignMobile,
      verticalAlign,
      verticalAlignMobile,
    };

    if (publish) {
      input.publishAt = publishAt || moment().toISOString();
      onChangePublishAt(input.publishAt);
    } else {
      input.publishAt = null;
      onChangePublishAt(null);
    }

    await updateCover({
      variables: {
        id: contextCoverId,
        input,
      },
    });
  };

  return (
    <Dialog open={true} onClose={handleClose} fullScreen>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSave(true);
        }}
      >
        <Content>
          <DialogHeader
            title={t("platformCustomCovers.editor.title")}
            actions={[
              <Button
                variant="contained"
                type="submit"
                disabled={updating}
                onClick={() => handleSave(true)}
              >
                {t(PRIMARY_ACTION_LABEL_KEYS[state])}
              </Button>,
              <Button
                variant="outlined"
                type="button"
                disabled={updating}
                onClick={() => handleSave(false)}
              >
                {t(SECONDARY_ACTION_LABEL_KEYS[state])}
              </Button>,
            ]}
            loading={loading || updating}
            onClose={handleClose}
          >
            <Stack flex={1} direction="row" justifyContent="space-between">
              <EditorToolbar />
              <DeviceModeToolbar />
            </Stack>
          </DialogHeader>
          <DialogContent sx={{ p: 1 }}>
            <CustomCoverPreview />
          </DialogContent>
          <EditDrawer />
        </Content>
      </form>
    </Dialog>
  );
};

interface CustomCoverEditorProps {
  coverId: string;
  open: boolean;
  onClose: () => void;
  onSave: () => void;
}

export const CustomCoverEditor: React.FC<CustomCoverEditorProps> = ({
  coverId,
  open,
  onClose,
  onSave,
}) => {
  if (!open) {
    return null;
  }

  return (
    <CustomCoverProvider>
      <CustomCoverEditorInner
        coverId={coverId}
        onClose={onClose}
        onSave={onSave}
      />
    </CustomCoverProvider>
  );
};
